import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
/**
 * Card de Estilo do Botão de Ação.
 *
 * Edita a APARÊNCIA de duas coisas distintas:
 * 1. o botão primário (`actionButton.style.*`) — sempre visível, porque é o que
 *    o modo "sobre o vídeo" e os vídeos legados usam;
 * 2. o bloco de oferta (`actionButton.template`) — só no modo "abaixo do vídeo".
 *
 * Divisão de responsabilidade com o card de Conteúdo: texto e tamanho moram lá,
 * cor e espessura moram aqui. Nenhuma propriedade é editável nos dois.
 */

import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import {
  Palette,
  Square,
  ChevronDown,
  Info,
  Sparkles,
  Layers,
  Type,
  BarChart3,
  CornerDownRight,
} from 'lucide-react';
import { ActionButton } from '@/store/videoFeatures';
import { ColorPicker } from '@/components/ui/color-picker';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { hasSlot, modelSpec } from '@/lib/action-button';
import type {
  ActionButtonSecondary,
  ActionButtonTextElement,
  ActionButtonTextWeight,
  ActionButtonToggleableText,
} from '@/store/videoFeatures/types/action-button-templates';
import {
  TEXT_SLOT_LABELS,
  isTextSlot,
  secondarySectionLabel,
  type TextSlot,
} from './slot-labels';

const WEIGHT_OPTIONS: { value: ActionButtonTextWeight; label: string }[] = [
  { value: 'normal', get label() { return translateInterfaceText("Normal"); } },
  { value: 'medium', get label() { return translateInterfaceText("Médio"); } },
  { value: 'semibold', get label() { return translateInterfaceText("Semi-negrito"); } },
  { value: 'bold', get label() { return translateInterfaceText("Negrito"); } },
  { value: 'extra-bold', get label() { return translateInterfaceText("Extra-negrito"); } },
];

interface ButtonStyleCardProps {
  actionButton: Partial<ActionButton>;
  updateStyleProperty: (property: string, value: any) => void;
  /** Grava um campo do bloco de oferta. */
  updateTemplate: (key: string, value: any) => void;
  /** false na aba "sobre o vídeo": esconde os cards do bloco de oferta. */
  showTemplates?: boolean;
}

export const ButtonStyleCard: React.FC<ButtonStyleCardProps> = ({
  actionButton,
  updateStyleProperty,
  updateTemplate,
  showTemplates = true,
}) => {
  useLanguage();
  const [isRoundedCornersOpen, setIsRoundedCornersOpen] = useState(true);
  const [isPreviewHovered, setIsPreviewHovered] = useState(false);
  // `??` e não `||`: raio 0 (canto reto) é escolha válida do usuário.
  const currentRadius = actionButton.style?.borderRadius ?? 8;

  const template = actionButton.template || {};
  const model = template.model;
  const spec = modelSpec(model);
  const specDefaults = spec.defaults;

  const textElement = (slot: TextSlot): ActionButtonTextElement => template[slot] || {};

  // Quais textos aparecem — e em que ordem — sai do layout do modelo, nunca de
  // um switch por modelo. Slot desligado no card de Conteúdo some daqui também:
  // não faz sentido escolher a cor de um texto que não vai ao ar.
  const textSlots = spec.layout
    .filter(isTextSlot)
    .filter(
      (slot) => (textElement(slot) as ActionButtonToggleableText).enabled !== false,
    );

  /** Grava um campo de um elemento textual preservando o resto (inclusive `enabled`). */
  const patchTextElement = (slot: TextSlot, patch: Partial<ActionButtonTextElement>) =>
    updateTemplate(slot, { ...textElement(slot), ...patch });

  const progressBar = template.progressBar || {};
  const progressDefaults = specDefaults.progressBar || {};
  const patchProgressBar = (patch: Record<string, unknown>) =>
    updateTemplate('progressBar', { ...progressBar, ...patch });

  const secondary: ActionButtonSecondary = template.secondary || {};
  const secondaryDefaults: ActionButtonSecondary = specDefaults.secondary || {};
  const patchSecondary = (patch: Partial<ActionButtonSecondary>) =>
    updateTemplate('secondary', { ...secondary, ...patch });

  // O modelo diz SE existe segundo caminho; `renderAs` (editável no card de
  // Conteúdo) diz a FORMA. Mesma resolução no card de Conteúdo e no render.
  const secondaryAs = spec.secondaryAs ? secondary.renderAs || spec.secondaryAs : null;

  // Efeitos do botão — mesma estrutura do grid de "Efeitos e Recursos" do
  // autoplay (AutoplayStyleSection). `shadow` tem default true porque a sombra
  // já era aplicada de forma fixa no player; `pulse` é opt-in.
  const effects = [
    {
      key: 'shadow',
      icon: Layers,
      label: translateInterfaceText("Sombra"),
      tooltip: translateInterfaceText("Aplica uma sombra sob o botão para destacá-lo do fundo"),
      checked: actionButton.style?.shadow !== false,
      onChange: (value: boolean) => updateStyleProperty('shadow', value),
    },
    {
      key: 'pulse',
      icon: Sparkles,
      label: translateInterfaceText("Pulsar"),
      tooltip: translateInterfaceText("Anima o botão em um pulso contínuo para chamar atenção"),
      checked: actionButton.style?.pulse === true,
      onChange: (value: boolean) => updateStyleProperty('pulse', value),
    },
  ];

  return (
    <div className="space-y-5">
      {/* Card de Cores - Premium */}
      <Card className="border-border bg-card shadow-sm">
        <CardContent className="p-6 space-y-6">
          {/* Header */}
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Palette className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold text-[#0F172A]">{translateInterfaceText("Cores do Botão")}</h3>
            </div>
            <p className="text-xs text-muted-foreground">
              {translateInterfaceText("Defina as cores do botão e do texto em estado normal e ao passar o mouse.")}</p>
          </div>

          {/* Seção 1: Estado Normal */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {translateInterfaceText("Estado Normal")}</span>
              <div className="h-px flex-1 bg-border" />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">{translateInterfaceText("Cor do Fundo")}</Label>
                <ColorPicker
                  color={actionButton.style?.backgroundColor || '#1D4FFF'}
                  onChange={(color) => updateStyleProperty('backgroundColor', color)}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">{translateInterfaceText("Cor do Texto")}</Label>
                <ColorPicker
                  color={actionButton.style?.textColor || '#FFFFFF'}
                  onChange={(color) => updateStyleProperty('textColor', color)}
                />
              </div>
            </div>
          </div>

          {/* Seção 2: Hover */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {translateInterfaceText("Hover")}</span>
              <div className="h-px flex-1 bg-border" />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">{translateInterfaceText("Cor do Fundo")}</Label>
                <ColorPicker
                  color={actionButton.style?.hoverBackgroundColor || '#1A47E5'}
                  onChange={(color) => updateStyleProperty('hoverBackgroundColor', color)}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">{translateInterfaceText("Cor do Texto")}</Label>
                <ColorPicker
                  color={actionButton.style?.hoverTextColor || '#FFFFFF'}
                  onChange={(color) => updateStyleProperty('hoverTextColor', color)}
                />
              </div>
            </div>
            
            {/* Helper text */}
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Info className="h-3.5 w-3.5 flex-shrink-0" />
              {translateInterfaceText("Recomendamos usar um tom levemente mais escuro para o hover.")}</p>
          </div>
        </CardContent>
      </Card>

      {/* Card de Cantos Arredondados */}
      <Card className="border-border bg-card shadow-sm">
        <CardContent className="p-6 space-y-4">
          {/* Header clicável */}
          <button
            type="button"
            onClick={() => setIsRoundedCornersOpen(!isRoundedCornersOpen)}
            className="w-full flex items-center justify-between cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <Square className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold text-[#0F172A]">{translateInterfaceText("Cantos Arredondados")}</h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{currentRadius}px</span>
              <ChevronDown 
                className={cn(
                  "h-5 w-5 text-muted-foreground transition-transform duration-200",
                  isRoundedCornersOpen && "rotate-180"
                )} 
              />
            </div>
          </button>

          {/* Conteúdo expandido inline */}
          {isRoundedCornersOpen && (
            <div className="pt-4 space-y-5 animate-in slide-in-from-top-2 duration-200">
              {/* Slider */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">{translateInterfaceText("Raio dos Cantos")}</Label>
                  <span className="text-sm font-semibold text-primary">{currentRadius}px</span>
                </div>
                
                <Slider
                  value={[currentRadius]}
                  onValueChange={(values) => updateStyleProperty('borderRadius', values[0])}
                  min={0}
                  max={30}
                  step={1}
                  className="w-full"
                />
                
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Card de Efeitos — mesmo padrão do grid do Autoplay */}
      <Card className="border-border bg-card shadow-sm">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center bg-[#1D4FFF]/10 text-[#1D4FFF]">
              <Sparkles className="w-4 h-4" strokeWidth={2} />
            </div>
            <div className="text-left">
              <span className="text-[13px] font-semibold text-[#1A1A1A] block">{translateInterfaceText("Efeitos")}</span>
              <span className="text-[11px] text-[#6B7280]">{translateInterfaceText("Ative comportamentos extras do botão")}</span>
            </div>
          </div>

          <div className="space-y-2">
            {effects.map(({ key, icon: Icon, label, tooltip, checked, onChange }) => (
              <div
                key={key}
                className="flex items-center justify-between p-3 rounded-xl border border-border bg-background"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Icon className={cn(
                    "h-4 w-4 flex-shrink-0 transition-colors",
                    checked ? "text-primary" : "text-muted-foreground"
                  )} />
                  <span className="text-sm font-medium text-foreground truncate">{label}</span>
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3.5 w-3.5 text-muted-foreground/60 cursor-help flex-shrink-0" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs max-w-[200px]">
                        {tooltip}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Switch checked={checked} onCheckedChange={onChange} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* --- Bloco de oferta (só no modo "abaixo do vídeo") --- */}

      {/* Aparência dos textos declarados no layout do modelo */}
      {showTemplates && textSlots.length > 0 && (
        <Card className="border-border bg-card shadow-sm">
          <CardContent className="p-6 space-y-5">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Type className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-[#0F172A]">{translateInterfaceText("Textos do bloco")}</h3>
              </div>
              <p className="text-xs text-muted-foreground">
                {translateInterfaceText("Cor e espessura de cada texto. O tamanho é definido no card de Conteúdo.")}</p>
            </div>

            <div className="space-y-5">
              {textSlots.map((slot) => (
                <div key={slot} className="space-y-2">
                  <Label className="text-sm font-medium">{TEXT_SLOT_LABELS[slot]}</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <ColorPicker
                      color={
                        textElement(slot).color ||
                        specDefaults[slot]?.color ||
                        '#0F172A'
                      }
                      onChange={(color) => patchTextElement(slot, { color })}
                    />
                    <Select
                      value={
                        textElement(slot).weight ||
                        specDefaults[slot]?.weight ||
                        'normal'
                      }
                      onValueChange={(weight: ActionButtonTextWeight) =>
                        patchTextElement(slot, { weight })
                      }
                    >
                      <SelectTrigger className="h-[44px]">
                        <SelectValue placeholder={translateInterfaceText("Espessura")} />
                      </SelectTrigger>
                      <SelectContent>
                        {WEIGHT_OPTIONS.map(({ value, label }) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cores da barra de progresso */}
      {showTemplates && hasSlot(model, 'progressBar') && (
        <Card className="border-border bg-card shadow-sm">
          <CardContent className="p-6 space-y-5">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-[#0F172A]">{translateInterfaceText("Barra de progresso")}</h3>
              </div>
              <p className="text-xs text-muted-foreground">
                {translateInterfaceText("Cores do trilho, do preenchimento e do texto exibido sobre a barra.")}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">{translateInterfaceText("Trilho")}</Label>
                <ColorPicker
                  color={progressBar.trackColor || progressDefaults.trackColor || '#E2E8F0'}
                  onChange={(trackColor) => patchProgressBar({ trackColor })}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">{translateInterfaceText("Preenchimento")}</Label>
                <ColorPicker
                  color={progressBar.fillColor || progressDefaults.fillColor || '#DC2626'}
                  onChange={(fillColor) => patchProgressBar({ fillColor })}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">{translateInterfaceText("Texto sobre a barra")}</Label>
                <ColorPicker
                  color={progressBar.textColor || progressDefaults.textColor || '#FFFFFF'}
                  onChange={(textColor) => patchProgressBar({ textColor })}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Segundo caminho: botão de recusa ou link discreto, conforme o modelo */}
      {showTemplates && secondaryAs && (
        <Card className="border-border bg-card shadow-sm">
          <CardContent className="p-6 space-y-5">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <CornerDownRight className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-[#0F172A]">
                  {secondarySectionLabel(secondaryAs)}
                </h3>
              </div>
              <p className="text-xs text-muted-foreground">
                {secondaryAs === 'button'
                  ? translateInterfaceText("Cores do segundo botão em estado normal e ao passar o mouse.")
                  : translateInterfaceText("Aparência do link de recusa exibido abaixo do botão.")}
              </p>
            </div>

            {secondaryAs === 'button' ? (
              <>
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <div className="h-px flex-1 bg-border" />
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {translateInterfaceText("Estado Normal")}</span>
                    <div className="h-px flex-1 bg-border" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">{translateInterfaceText("Cor do Fundo")}</Label>
                      <ColorPicker
                        color={
                          secondary.backgroundColor ||
                          secondaryDefaults.backgroundColor ||
                          '#F1F5F9'
                        }
                        onChange={(backgroundColor) => patchSecondary({ backgroundColor })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">{translateInterfaceText("Cor do Texto")}</Label>
                      <ColorPicker
                        color={secondary.color || secondaryDefaults.color || '#0F172A'}
                        onChange={(color) => patchSecondary({ color })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">{translateInterfaceText("Cor da Borda")}</Label>
                      <ColorPicker
                        color={
                          secondary.borderColor || secondaryDefaults.borderColor || '#E2E8F0'
                        }
                        onChange={(borderColor) => patchSecondary({ borderColor })}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <div className="h-px flex-1 bg-border" />
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {translateInterfaceText("Hover")}</span>
                    <div className="h-px flex-1 bg-border" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">{translateInterfaceText("Cor do Fundo")}</Label>
                      <ColorPicker
                        color={
                          secondary.hoverBackgroundColor ||
                          secondaryDefaults.hoverBackgroundColor ||
                          '#E2E8F0'
                        }
                        onChange={(hoverBackgroundColor) =>
                          patchSecondary({ hoverBackgroundColor })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">{translateInterfaceText("Cor do Texto")}</Label>
                      <ColorPicker
                        color={
                          secondary.hoverColor || secondaryDefaults.hoverColor || '#0F172A'
                        }
                        onChange={(hoverColor) => patchSecondary({ hoverColor })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">{translateInterfaceText("Cor da Borda")}</Label>
                      <ColorPicker
                        color={
                          secondary.hoverBorderColor ||
                          secondaryDefaults.hoverBorderColor ||
                          '#CBD5E1'
                        }
                        onChange={(hoverBorderColor) => patchSecondary({ hoverBorderColor })}
                      />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{translateInterfaceText("Cor do Texto")}</Label>
                    <ColorPicker
                      color={secondary.color || secondaryDefaults.color || '#64748B'}
                      onChange={(color) => patchSecondary({ color })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{translateInterfaceText("Cor ao passar o mouse")}</Label>
                    <ColorPicker
                      color={secondary.hoverColor || secondaryDefaults.hoverColor || '#DC2626'}
                      onChange={(hoverColor) => patchSecondary({ hoverColor })}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-xl border border-border bg-background">
                  <span className="text-sm font-medium text-foreground">{translateInterfaceText("Sublinhado")}</span>
                  <Switch
                    checked={secondary.underline ?? secondaryDefaults.underline ?? true}
                    onCheckedChange={(underline) => patchSecondary({ underline })}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
